import { useCallback, useEffect, useMemo, useState } from 'react';

import AcademiaSelect from '../common/AcademiaSelect';
import ElearningFileUpload from '../elearning/ElearningFileUpload';
import ElearningExerciseEditor, { exerciseQuestionsToJson, jsonToExerciseQuestions, type ExerciseQuestion } from '../elearning/ElearningExerciseEditor';
import AdminConfirmDialog from '../admin/AdminConfirmDialog';
import AdminToast from '../admin/AdminToast';
import FormateurPageShell from './FormateurPageShell';
import { FormateurElearningActionsMenu } from './FormateurElearningActionsMenu';
import { AcademicPanelCard, AcademicPanelListItem } from '../admin/academia/AcademicPanelCard';
import {
  correctFormateurExerciceReponse,
  createFormateurElearningCours,
  createFormateurElearningRessource,
  deleteFormateurElearningCours,
  deleteFormateurElearningRessource,
  fetchFormateurElearningCours,
  fetchFormateurElearningFilieres,
  fetchFormateurElearningForumMessages,
  fetchFormateurElearningForums,
  fetchFormateurElearningRessources,
  fetchFormateurExercicesEnAttente,
  moderateFormateurElearningForumMessage,
  postFormateurElearningForumMessage,
  updateFormateurElearningCours,
  updateFormateurElearningRessource,
} from '../../services/academiaFormateurService';

const fieldClass =
  'w-full rounded-lg border border-outline-variant/30 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15';

const FORUM_MESSAGE_STATUT_LABELS: Record<string, string> = {
  VISIBLE: 'Visible',
  MASQUE: 'Masqué',
  SUPPRIME: 'Supprimé',
};

type TabId = 'cours' | 'ressources' | 'corrections' | 'forums' | 'moderation';

type PendingCorrection = {
  id: string;
  reponsesJson?: Record<string, unknown>;
  ressource?: { id: string; titre: string; contenuJson?: unknown };
  apprenant?: string;
};

export default function FormateurElearningPanel() {
  const [tab, setTab] = useState<TabId>('ressources');
  const [filieres, setFilieres] = useState<Array<{ id: string; libelle: string; code: string }>>([]);
  const [idFiliere, setIdFiliere] = useState('');
  const [cours, setCours] = useState<Array<Record<string, unknown>>>([]);
  const [ressources, setRessources] = useState<Array<Record<string, unknown>>>([]);
  const [pendingCorrections, setPendingCorrections] = useState<PendingCorrection[]>([]);
  const [selectedCorrectionId, setSelectedCorrectionId] = useState('');
  const [questionPoints, setQuestionPoints] = useState<Record<string, string>>({});
  const [correctionFeedback, setCorrectionFeedback] = useState('');
  const [forums, setForums] = useState<Array<Record<string, unknown>>>([]);
  const [selectedForumId, setSelectedForumId] = useState('');
  const [forumMessages, setForumMessages] = useState<Array<Record<string, unknown>>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ type: 'VIDEO', titre: '', storageKey: '', idCours: '', estPublie: true });
  const [coursForm, setCoursForm] = useState({ titre: '', description: '', estPublie: false });
  const [exerciseQuestions, setExerciseQuestions] = useState<ExerciseQuestion[]>([]);
  const [reply, setReply] = useState('');
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editCoursId, setEditCoursId] = useState('');
  const [editRessourceId, setEditRessourceId] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ kind: 'cours' | 'ressource'; id: string; titre: string } | null>(null);
  const [openActionsMenuKey, setOpenActionsMenuKey] = useState<string | null>(null);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const selectedCorrection = useMemo(
    () => pendingCorrections.find((c) => c.id === selectedCorrectionId) ?? null,
    [pendingCorrections, selectedCorrectionId],
  );

  const correctionQuestions = useMemo(() => {
    if (!selectedCorrection?.ressource?.contenuJson) return [];
    return jsonToExerciseQuestions(selectedCorrection.ressource.contenuJson);
  }, [selectedCorrection]);

  const coursOptions = useMemo(
    () => [{ value: '', label: '— Hors cours —' }, ...cours.map((c) => ({ value: String(c.id), label: String(c.titre) }))],
    [cours],
  );

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const f = await fetchFormateurElearningFilieres();
      setFilieres(f);
      const fid = idFiliere || f[0]?.id || '';
      if (fid && fid !== idFiliere) setIdFiliere(fid);
      if (fid) {
        const [r, fo, c, pending] = await Promise.all([
          fetchFormateurElearningRessources(fid),
          fetchFormateurElearningForums(fid),
          fetchFormateurElearningCours(fid),
          fetchFormateurExercicesEnAttente(fid),
        ]);
        setRessources(r);
        setForums(fo);
        setCours(c);
        setPendingCorrections(pending);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [idFiliere]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!selectedCorrection) {
      setQuestionPoints({});
      setCorrectionFeedback('');
      return;
    }
    const reponses = (selectedCorrection.reponsesJson ?? {}) as Record<string, unknown>;
    const initial: Record<string, string> = {};
    for (const q of jsonToExerciseQuestions(selectedCorrection.ressource?.contenuJson)) {
      if (q.type === 'TEXTE_LIBRE' || reponses[q.id] != null) {
        initial[q.id] = '0';
      }
    }
    setQuestionPoints(initial);
    setCorrectionFeedback('');
  }, [selectedCorrection]);

  async function submitRessource(e: React.FormEvent) {
    e.preventDefault();
    if (!idFiliere || !form.titre.trim()) return;
    if (!editRessourceId && form.type !== 'EXERCICE' && !form.storageKey) {
      showToast('Téléversez un fichier avant publication.', 'error');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        idCours: form.idCours || null,
        titre: form.titre.trim(),
        storageKey: form.storageKey || undefined,
        contenuJson: form.type === 'EXERCICE' ? exerciseQuestionsToJson(exerciseQuestions) : undefined,
        estPublie: form.estPublie,
      };
      if (editRessourceId) {
        await updateFormateurElearningRessource(editRessourceId, payload);
      } else {
        await createFormateurElearningRessource({
          idFiliere,
          type: form.type,
          ...payload,
        });
      }
      resetRessourceForm();
      await load();
      showToast(editRessourceId ? 'Ressource mise à jour.' : 'Ressource publiée avec succès.', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Impossible d’enregistrer la ressource.', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  function resetRessourceForm() {
    setForm({ type: 'VIDEO', titre: '', storageKey: '', idCours: '', estPublie: true });
    setExerciseQuestions([]);
    setEditRessourceId('');
  }

  function startEditRessource(r: Record<string, unknown>) {
    setEditRessourceId(String(r.id));
    setForm({
      type: String(r.type ?? 'VIDEO'),
      titre: String(r.titre ?? ''),
      storageKey: String(r.storageKey ?? ''),
      idCours: r.idCours ? String(r.idCours) : '',
      estPublie: Boolean(r.estPublie),
    });
    setExerciseQuestions(
      String(r.type) === 'EXERCICE' ? jsonToExerciseQuestions(r.contenuJson) : [],
    );
  }

  async function toggleRessourcePublish(r: Record<string, unknown>) {
    setIsSaving(true);
    try {
      await updateFormateurElearningRessource(String(r.id), { estPublie: !r.estPublie });
      await load();
      showToast(r.estPublie ? 'Ressource dépubliée.' : 'Ressource publiée.', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Mise à jour impossible.', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setIsSaving(true);
    try {
      if (deleteTarget.kind === 'cours') {
        await deleteFormateurElearningCours(deleteTarget.id);
        if (editCoursId === deleteTarget.id) resetCoursForm();
      } else {
        await deleteFormateurElearningRessource(deleteTarget.id);
        if (editRessourceId === deleteTarget.id) resetRessourceForm();
      }
      setDeleteTarget(null);
      await load();
      showToast('Élément supprimé.', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Suppression impossible.', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  async function submitCours(e: React.FormEvent) {
    e.preventDefault();
    if (!idFiliere || !coursForm.titre.trim()) return;
    setIsSaving(true);
    try {
      if (editCoursId) {
        await updateFormateurElearningCours(editCoursId, {
          titre: coursForm.titre.trim(),
          description: coursForm.description.trim() || null,
          estPublie: coursForm.estPublie,
        });
      } else {
        await createFormateurElearningCours({ idFiliere, ...coursForm, titre: coursForm.titre.trim() });
      }
      resetCoursForm();
      await load();
      showToast(editCoursId ? 'Cours mis à jour.' : 'Cours créé avec succès.', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Impossible d’enregistrer le cours.', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  function resetCoursForm() {
    setCoursForm({ titre: '', description: '', estPublie: false });
    setEditCoursId('');
  }

  function startEditCours(c: Record<string, unknown>) {
    setEditCoursId(String(c.id));
    setCoursForm({
      titre: String(c.titre ?? ''),
      description: String(c.description ?? ''),
      estPublie: Boolean(c.estPublie),
    });
  }

  async function toggleCoursPublish(c: Record<string, unknown>) {
    setIsSaving(true);
    try {
      await updateFormateurElearningCours(String(c.id), { estPublie: !c.estPublie });
      await load();
      showToast(c.estPublie ? 'Cours dépublié.' : 'Cours publié.', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Mise à jour impossible.', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  async function submitCorrection(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedCorrectionId) return;
    const questionCorrections: Record<string, { points: number; feedback?: string }> = {};
    for (const [qid, pts] of Object.entries(questionPoints)) {
      questionCorrections[qid] = { points: Number(pts) || 0 };
    }
    setIsSaving(true);
    try {
      await correctFormateurExerciceReponse(selectedCorrectionId, {
        questionCorrections,
        feedbackFormateur: correctionFeedback.trim() || null,
      });
      setSelectedCorrectionId('');
      await load();
      showToast('Correction enregistrée — l’apprenant sera notifié.', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Impossible d’enregistrer la correction.', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleModerateMessage(messageId: string, statut: 'MASQUE' | 'SUPPRIME' | 'VISIBLE') {
    setIsSaving(true);
    try {
      await moderateFormateurElearningForumMessage(messageId, { statut });
      if (selectedForumId) {
        setForumMessages(await fetchFormateurElearningForumMessages(selectedForumId));
      }
      showToast(
        statut === 'MASQUE'
          ? 'Message masqué.'
          : statut === 'VISIBLE'
            ? 'Message rendu visible.'
            : 'Message supprimé.',
        'success',
      );
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Modération impossible.', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleForumReply(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedForumId || !reply.trim()) return;
    setIsSaving(true);
    try {
      await postFormateurElearningForumMessage(selectedForumId, { contenu: reply });
      setReply('');
      setForumMessages(await fetchFormateurElearningForumMessages(selectedForumId));
      showToast('Réponse publiée.', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Impossible de publier la réponse.', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <>
      <AdminToast
        message={toast?.message ?? null}
        variant={toast?.variant ?? 'success'}
        onClose={dismissToast}
      />
      <FormateurPageShell
      title="E-learning"
      description="Cours, ressources, correction des exercices et modération des forums."
      isLoading={loading}
      isError={Boolean(error)}
      errorMessage={error}
      onRetry={() => void load()}
      sideCard={{
        label: 'Corrections en attente',
        value: String(pendingCorrections.length),
        helper: 'Réponses libres à valider',
        sideLinks: [
          { label: 'Mes sessions', to: '/formateur/sessions', icon: 'event' },
          { label: 'Accueil', to: '/formateur', icon: 'dashboard' },
        ],
      }}
    >
      <div className="mb-4 flex flex-wrap gap-2">
        {(['cours', 'ressources', 'corrections', 'forums', 'moderation'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold ${tab === t ? 'bg-primary text-white' : 'bg-surface-container-low'}`}
          >
            {t === 'cours' ? 'Cours' : t === 'ressources' ? 'Ressources' : t === 'corrections' ? `Corrections (${pendingCorrections.length})` : t === 'forums' ? 'Forums' : 'Modération'}
          </button>
        ))}
      </div>

      {filieres.length > 0 ? (
        <div className="mb-4 max-w-md">
          <AcademiaSelect value={idFiliere} onChange={(e) => setIdFiliere(e.target.value)}>
            {filieres.map((f) => (
              <option key={f.id} value={f.id}>
                {f.code} — {f.libelle}
              </option>
            ))}
          </AcademiaSelect>
        </div>
      ) : null}

      {tab === 'cours' ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <AcademicPanelCard title="Mes cours en ligne" icon="menu_book">
            <ul className="academic-panel-list">
              {cours.map((c) => (
                <AcademicPanelListItem key={String(c.id)}>
                  <div className="flex w-full items-start justify-between gap-3">
                    <div>
                      <span className="block font-medium text-slate-900">{String(c.titre)}</span>
                      <span className="text-xs text-on-surface-variant">
                        {String(c.ressourcesCount ?? 0)} ressource(s) · {c.estPublie ? 'Publié' : 'Brouillon'}
                        {c.description ? ` · ${String(c.description).slice(0, 60)}${String(c.description).length > 60 ? '…' : ''}` : ''}
                      </span>
                    </div>
                    <FormateurElearningActionsMenu
                      menuKey={`cours:${String(c.id)}`}
                      openMenuKey={openActionsMenuKey}
                      onOpenMenuKeyChange={setOpenActionsMenuKey}
                      items={[
                        {
                          id: 'edit',
                          label: 'Voir / Modifier',
                          icon: 'edit',
                          onClick: () => startEditCours(c),
                        },
                        {
                          id: 'publish',
                          label: c.estPublie ? 'Dépublier' : 'Publier',
                          icon: c.estPublie ? 'visibility_off' : 'check_circle',
                          disabled: isSaving,
                          onClick: () => void toggleCoursPublish(c),
                        },
                        {
                          id: 'delete',
                          label: 'Supprimer',
                          icon: 'delete_forever',
                          variant: 'danger',
                          onClick: () =>
                            setDeleteTarget({ kind: 'cours', id: String(c.id), titre: String(c.titre) }),
                        },
                      ]}
                    />
                  </div>
                </AcademicPanelListItem>
              ))}
              {cours.length === 0 ? <p className="text-sm text-on-surface-variant">Aucun cours pour cette filière.</p> : null}
            </ul>
          </AcademicPanelCard>
          <AcademicPanelCard title={editCoursId ? 'Modifier le cours' : 'Nouveau cours'} icon={editCoursId ? 'edit' : 'add'}>
            <form className="space-y-3" onSubmit={submitCours}>
              <input className={fieldClass} placeholder="Titre du cours" value={coursForm.titre} onChange={(e) => setCoursForm({ ...coursForm, titre: e.target.value })} required />
              <textarea className={fieldClass} rows={3} placeholder="Description" value={coursForm.description} onChange={(e) => setCoursForm({ ...coursForm, description: e.target.value })} />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={coursForm.estPublie} onChange={(e) => setCoursForm({ ...coursForm, estPublie: e.target.checked })} />
                Publier immédiatement
              </label>
              <div className="flex gap-2">
                {editCoursId ? (
                  <button type="button" className="academic-panel-btn academic-panel-btn--secondary flex-1 !justify-center" onClick={resetCoursForm}>
                    Annuler
                  </button>
                ) : null}
                <button
                  type="submit"
                  disabled={isSaving}
                  className="academic-panel-btn academic-panel-btn--primary flex-1 !justify-center disabled:opacity-60"
                >
                  {isSaving ? 'Enregistrement…' : editCoursId ? 'Enregistrer' : 'Créer le cours'}
                </button>
              </div>
            </form>
          </AcademicPanelCard>
        </div>
      ) : null}

      {tab === 'ressources' ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <AcademicPanelCard title="Mes ressources" icon="video">
            <ul className="academic-panel-list">
            {ressources.map((r) => (
              <AcademicPanelListItem key={String(r.id)}>
                <div className="flex w-full items-start justify-between gap-3">
                  <div>
                    <span className="block font-medium text-slate-900">{String(r.titre)}</span>
                    <span className="text-xs text-on-surface-variant">
                      {String(r.type)}
                      {r.estPublie ? '' : ' · brouillon'}
                      {r.type === 'VIDEO' && r.transcodageStatut ? ` · ${String(r.transcodageStatut)}` : ''}
                      {r.storageKey ? ` · fichier joint` : ''}
                    </span>
                  </div>
                  <FormateurElearningActionsMenu
                    menuKey={`ressource:${String(r.id)}`}
                    openMenuKey={openActionsMenuKey}
                    onOpenMenuKeyChange={setOpenActionsMenuKey}
                    items={[
                      {
                        id: 'edit',
                        label: 'Voir / Modifier',
                        icon: 'edit',
                        onClick: () => startEditRessource(r),
                      },
                      {
                        id: 'publish',
                        label: r.estPublie ? 'Dépublier' : 'Publier',
                        icon: r.estPublie ? 'visibility_off' : 'check_circle',
                        disabled: isSaving,
                        onClick: () => void toggleRessourcePublish(r),
                      },
                      {
                        id: 'delete',
                        label: 'Supprimer',
                        icon: 'delete_forever',
                        variant: 'danger',
                        onClick: () =>
                          setDeleteTarget({ kind: 'ressource', id: String(r.id), titre: String(r.titre) }),
                      },
                    ]}
                  />
                </div>
              </AcademicPanelListItem>
            ))}
            </ul>
            {ressources.length === 0 ? <p className="text-sm text-on-surface-variant">Aucune ressource pour cette filière.</p> : null}
          </AcademicPanelCard>
          <AcademicPanelCard title={editRessourceId ? 'Modifier la ressource' : 'Nouvelle ressource'} icon={editRessourceId ? 'edit' : 'add'}>
            <form className="space-y-3" onSubmit={submitRessource}>
              <AcademiaSelect
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                disabled={Boolean(editRessourceId)}
              >
                <option value="VIDEO">Vidéo</option>
                <option value="SUPPORT">Support</option>
                <option value="EXERCICE">Exercice</option>
              </AcademiaSelect>
              <AcademiaSelect value={form.idCours} onChange={(e) => setForm({ ...form, idCours: e.target.value })}>
                {coursOptions.map((o) => (
                  <option key={o.value || 'none'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </AcademiaSelect>
              <input className={fieldClass} placeholder="Titre" value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} required />
              {form.type !== 'EXERCICE' ? (
                <>
                  {form.storageKey ? (
                    <p className="text-xs text-on-surface-variant">Fichier actuel : {form.storageKey.split('/').pop()}</p>
                  ) : null}
                  <ElearningFileUpload uploadPath="/formateur/elearning/upload" onUploaded={({ storageKey }) => setForm({ ...form, storageKey })} />
                </>
              ) : null}
              {form.type === 'EXERCICE' ? <ElearningExerciseEditor value={exerciseQuestions} onChange={setExerciseQuestions} /> : null}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.estPublie} onChange={(e) => setForm({ ...form, estPublie: e.target.checked })} />
                Publier immédiatement
              </label>
              <div className="flex gap-2">
                {editRessourceId ? (
                  <button type="button" className="academic-panel-btn academic-panel-btn--secondary flex-1 !justify-center" onClick={resetRessourceForm}>
                    Annuler
                  </button>
                ) : null}
                <button
                  type="submit"
                  disabled={isSaving}
                  className="academic-panel-btn academic-panel-btn--primary flex-1 !justify-center disabled:opacity-60"
                >
                  {isSaving ? 'Enregistrement…' : editRessourceId ? 'Enregistrer' : 'Publier la ressource'}
                </button>
              </div>
            </form>
          </AcademicPanelCard>
        </div>
      ) : null}

      {tab === 'corrections' ? (
        <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
          <AcademicPanelCard title="En attente de correction" icon="edit_note">
            {pendingCorrections.length === 0 ? (
              <p className="text-sm text-on-surface-variant">Aucune réponse en attente.</p>
            ) : (
              pendingCorrections.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`mb-2 w-full rounded-lg border px-3 py-2 text-left text-sm ${selectedCorrectionId === item.id ? 'border-primary bg-primary/5' : 'border-outline-variant/20'}`}
                  onClick={() => setSelectedCorrectionId(item.id)}
                >
                  <span className="block font-semibold">{item.ressource?.titre ?? 'Exercice'}</span>
                  <span className="text-xs text-on-surface-variant">{item.apprenant ?? 'Apprenant'}</span>
                </button>
              ))
            )}
          </AcademicPanelCard>
          <AcademicPanelCard title="Corriger la réponse" icon="check_circle">
            {!selectedCorrection ? (
              <p className="text-sm text-on-surface-variant">Sélectionnez une soumission à corriger.</p>
            ) : (
              <form className="space-y-4" onSubmit={submitCorrection}>
                <p className="text-sm">
                  <span className="font-bold">{selectedCorrection.apprenant}</span>
                  {' · '}
                  {selectedCorrection.ressource?.titre}
                </p>
                {correctionQuestions.map((q) => {
                  const answer = (selectedCorrection.reponsesJson ?? {})[q.id];
                  if (q.type !== 'TEXTE_LIBRE' && answer == null) return null;
                  return (
                    <div key={q.id} className="rounded-lg border border-outline-variant/20 p-3">
                      <p className="text-sm font-semibold">{q.libelle}</p>
                      <p className="mt-1 rounded bg-surface-container-low p-2 text-sm">{String(answer ?? '—')}</p>
                      <label className="mt-2 block text-xs font-bold text-on-surface-variant">
                        Points (max {q.points ?? 1})
                        <input
                          type="number"
                          min={0}
                          max={q.points ?? 1}
                          step={0.5}
                          className={`${fieldClass} mt-1`}
                          value={questionPoints[q.id] ?? '0'}
                          onChange={(e) => setQuestionPoints({ ...questionPoints, [q.id]: e.target.value })}
                        />
                      </label>
                    </div>
                  );
                })}
                <textarea
                  className={fieldClass}
                  rows={3}
                  placeholder="Feedback global pour l'apprenant…"
                  value={correctionFeedback}
                  onChange={(e) => setCorrectionFeedback(e.target.value)}
                />
                <button
                  type="submit"
                  disabled={isSaving}
                  className="academic-panel-btn academic-panel-btn--primary disabled:opacity-60"
                >
                  {isSaving ? 'Enregistrement…' : 'Enregistrer la correction'}
                </button>
              </form>
            )}
          </AcademicPanelCard>
        </div>
      ) : null}

      {tab === 'forums' ? (
        <AcademicPanelCard title="Forums" icon="forum">
          <ul className="academic-panel-list">
          {forums.map((f) => (
            <AcademicPanelListItem key={String(f.id)}>
              <span>
                <span className="block font-medium text-slate-900">{String(f.titre)}</span>
                <span className="text-xs text-on-surface-variant">{String(f.messagesCount ?? 0)} messages</span>
              </span>
            </AcademicPanelListItem>
          ))}
          </ul>
        </AcademicPanelCard>
      ) : null}

      {tab === 'moderation' ? (
        <div className="grid gap-4 lg:grid-cols-[240px_1fr]">
          <AcademicPanelCard title="Forums" icon="forum">
            {forums.map((f) => (
              <button
                key={String(f.id)}
                type="button"
                className="mb-2 w-full rounded-lg border px-3 py-2 text-left text-sm"
                onClick={async () => {
                  setSelectedForumId(String(f.id));
                  setForumMessages(await fetchFormateurElearningForumMessages(String(f.id)));
                }}
              >
                {String(f.titre)}
              </button>
            ))}
          </AcademicPanelCard>
          <AcademicPanelCard title="Modération & réponses" icon="shield">
            {forumMessages.map((m) => {
              const statut = String(m.statut ?? 'VISIBLE');
              const isHidden = statut === 'MASQUE';
              const isDeleted = statut === 'SUPPRIME';
              return (
              <div
                key={String(m.id)}
                className={`mb-3 rounded-lg border p-3 text-sm ${isHidden ? 'border-amber-200 bg-amber-50/50' : ''} ${isDeleted ? 'border-red-200 bg-red-50/40 opacity-80' : ''}`}
              >
                <p className="text-xs font-bold">
                  {String(m.auteur)} · {FORUM_MESSAGE_STATUT_LABELS[statut] ?? statut}
                </p>
                <p>{String(m.contenu)}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {!isDeleted ? (
                    <button
                      type="button"
                      disabled={isSaving}
                      className={`text-xs underline disabled:opacity-50 ${isHidden ? 'text-amber-800' : 'text-primary'}`}
                      onClick={() =>
                        void handleModerateMessage(String(m.id), isHidden ? 'VISIBLE' : 'MASQUE')
                      }
                    >
                      {isHidden ? 'Rendre visible' : 'Masquer'}
                    </button>
                  ) : null}
                  {!isDeleted ? (
                    <button
                      type="button"
                      disabled={isSaving}
                      className="text-xs text-red-700 underline disabled:opacity-50"
                      onClick={() => void handleModerateMessage(String(m.id), 'SUPPRIME')}
                    >
                      Supprimer
                    </button>
                  ) : null}
                </div>
              </div>
            );
            })}
            {selectedForumId ? (
              <form className="mt-4 space-y-2" onSubmit={(e) => void handleForumReply(e)}>
                <textarea className={fieldClass} rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Réponse formateur…" />
                <button
                  type="submit"
                  disabled={isSaving || !reply.trim()}
                  className="academic-panel-btn academic-panel-btn--primary disabled:opacity-60"
                >
                  {isSaving ? 'Envoi…' : 'Répondre'}
                </button>
              </form>
            ) : null}
          </AcademicPanelCard>
        </div>
      ) : null}
    </FormateurPageShell>
      <AdminConfirmDialog
        open={Boolean(deleteTarget)}
        title={deleteTarget?.kind === 'cours' ? 'Supprimer le cours' : 'Supprimer la ressource'}
        description={
          <p>
            Confirmer la suppression de <strong>{deleteTarget?.titre}</strong> ? Cette action est irréversible.
          </p>
        }
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        variant="danger"
        isConfirming={isSaving}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          if (!isSaving) setDeleteTarget(null);
        }}
      />
    </>
  );
}
